import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { projectTypesAPI, projectsAPI, usersAPI } from '../utils/api';
import { PageHeader, Modal, FormField, ConfirmDialog } from '../components/layout/UI';
import { AnyPermissionGate } from '../components/PermissionGate';
import { useToast, apiError } from '../components/Toast';
import { useMaster } from '../hooks/useMasters';

const PlusIc = () => <Plus size={14}/>;
const PenIc  = () => <Pencil size={12}/>;
const TrIc   = () => <Trash2 size={12}/>;

const SEV_STYLE = {
  Critical:{ bg:'bg-red-100 dark:bg-red-900/30',    text:'text-red-700 dark:text-red-400',    border:'border-l-red-500' },
  High:    { bg:'bg-orange-100 dark:bg-orange-900/30',text:'text-orange-700 dark:text-orange-400',border:'border-l-orange-500' },
  Medium:  { bg:'bg-amber-100 dark:bg-amber-900/30', text:'text-amber-700 dark:text-amber-400', border:'border-l-amber-500' },
  Low:     { bg:'bg-slate-100 dark:bg-slate-800',    text:'text-slate-600 dark:text-slate-400', border:'border-l-slate-400' },
};
// Values are H/M/L — the words are only labels. Seeded that way in Masters.
const IMPACTS_FALLBACK = [{value:'H',label:'High'},{value:'M',label:'Medium'},{value:'L',label:'Low'}];
const CATEGORIES_FALLBACK = ['Technical','Operational','Financial','Schedule','Resource','Compliance','Other'];
const STATUSES_FALLBACK   = ['Open','Mitigating','Accepted','Closed'];

const defaultForm = { title:'',description:'',category:'Operational',impact:'M',probability:'M',mitigation_plan:'',contingency_plan:'',owner_id:'',status:'Open',review_date:'' };

export default function RisksPage() {
  // Admin-controlled via Masters; the _FALLBACK arrays cover a fresh install.
  const CATEGORY_OPTS = useMaster('risk_category', CATEGORIES_FALLBACK);
  const STATUS_OPTS   = useMaster('risk_status',   STATUSES_FALLBACK);
  const LEVEL_OPTS    = useMaster('risk_level',    IMPACTS_FALLBACK);
  // Status drives the filter pills and the counts, so both read the same list.
  const STATUSES      = STATUS_OPTS.map(o => o.value);
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [risks,    setRisks]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [users,    setUsers]    = useState([]);
  const [selProject,setSelProject]=useState(searchParams.get('project_id')||'');
  const [loading,  setLoading]  = useState(false);
  const [modalOpen,setModalOpen]= useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form,     setForm]     = useState({ ...defaultForm });
  const [saving,   setSaving]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('Open');
  const [error, setError] = useState('');
  const f = v => setForm(p => ({ ...p, ...v }));

  useEffect(() => {
    Promise.all([projectsAPI.getAll({ limit: 0 }), usersAPI.getAll()])
      .then(([pR,uR]) => { setProjects(pR.data); setUsers(uR.data); });
  }, []);

  const load = useCallback(async () => {
    if (!selProject) return;
    setLoading(true);
    try { const r = await projectTypesAPI.getRisks(selProject); setRisks(r.data); setError(''); }
    catch(e) { setError(e.response?.data?.message || 'Failed to load risks'); } finally { setLoading(false); }
  }, [selProject]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ ...defaultForm }); setModalOpen(true); };
  const openEdit   = (r) => {
    setEditItem(r);
    setForm({ title:r.title, description:r.description||'', category:r.category, impact:r.impact,
              probability:r.probability, mitigation_plan:r.mitigation_plan||'',
              contingency_plan:r.contingency_plan||'', owner_id:r.owner_id||'',
              status:r.status, review_date:r.review_date?.split('T')[0]||'' });
    setModalOpen(true);
  };
  const handleSave = async () => {
    if (!form.title||!selProject) return;
    setSaving(true);
    try {
      if (editItem) await projectTypesAPI.updateRisk(editItem.id, form);
      else          await projectTypesAPI.createRisk(selProject, form);
      setModalOpen(false); load();
    } catch(e) { toast.error(apiError(e, 'Failed to save risk.')); } finally { setSaving(false); }
  };
  const handleDelete = async () => {
    try { await projectTypesAPI.deleteRisk(deleteId); setDeleteId(null); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to delete risk')); }
  };

  const filtered = risks.filter(r => !statusFilter || r.status===statusFilter);
  const counts   = STATUSES.reduce((a,s) => { a[s]=risks.filter(r=>r.status===s).length; return a; }, {});

  return (
    <div>
      <PageHeader title="Risk Register" subtitle="Track and mitigate project-level risks"
        actions={selProject && <AnyPermissionGate projectId={selProject} permission="manage_risks" systemRoles={['Admin','Manager']}><button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIc /> Add Risk</button></AnyPermissionGate>}/>

      <div className="card mb-5">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex-1 min-w-48 max-w-xs">
            <label className="label">Project</label>
            <select className="select" value={selProject} onChange={e=>setSelProject(e.target.value)}>
              <option value="">Select a project...</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {selProject && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={()=>setStatusFilter('')} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${!statusFilter?'bg-brand-500 text-white border-brand-500':'border-[var(--border)]'}`} style={!statusFilter?{}:{ color:'var(--text-secondary)', background:'var(--surface)' }}>
                All ({risks.length})
              </button>
              {/* Filter by the stored value, label it with what the Admin chose. */}
              {STATUS_OPTS.map(o => (
                <button key={o.value} onClick={()=>setStatusFilter(o.value)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${statusFilter===o.value?'bg-brand-500 text-white border-brand-500':'border-[var(--border)]'}`}
                  style={statusFilter===o.value?{}:{ color:'var(--text-secondary)', background:'var(--surface)' }}>
                  {o.label} ({counts[o.value]||0})
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm mb-3" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}
      {!selProject ? (
        <div className="card text-center py-16"><p className="text-4xl mb-3">⚠️</p><p className="font-semibold" style={{ color:'var(--text)' }}>Select a Project</p></div>
      ) : loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : filtered.length===0 ? (
        <div className="card text-center py-16">
          <p className="text-4xl mb-3">✅</p>
          <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>No {statusFilter||''} risks</p>
          <p className="text-sm mb-4" style={{ color:'var(--text-secondary)' }}>Log risks to proactively manage potential threats to this project</p>
          <AnyPermissionGate projectId={selProject} permission="manage_risks" systemRoles={['Admin','Manager']}><button onClick={openCreate} className="btn-primary flex items-center gap-1.5 mx-auto"><PlusIc /> Add First Risk</button></AnyPermissionGate>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const sty = SEV_STYLE[r.severity]||SEV_STYLE['Low'];
            return (
              <div key={r.id} className={`card border-l-4 ${sty.border}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <span className="text-xs font-mono font-bold" style={{ color:'var(--text-secondary)' }}>{r.risk_code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${sty.bg} ${sty.text}`}>{r.severity}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{r.category}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status==='Closed'?'bg-slate-100 dark:bg-slate-800 text-slate-500':'bg-brand-50 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400'}`}>{r.status}</span>
                    </div>
                    <p className="font-semibold" style={{ color:'var(--text)' }}>{r.title}</p>
                    {r.description && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{r.description}</p>}
                    <div className="flex gap-4 mt-2 flex-wrap">
                      <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Impact: <strong className={sty.text}>{LEVEL_OPTS.find(o=>o.value===r.impact)?.label || r.impact}</strong></span>
                      <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Probability: <strong className={sty.text}>{LEVEL_OPTS.find(o=>o.value===r.probability)?.label || r.probability}</strong></span>
                      {r.owner_name && <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Owner: <strong style={{ color:'var(--text)' }}>{r.owner_name}</strong></span>}
                      {r.review_date && <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Review: {new Date(r.review_date).toLocaleDateString()}</span>}
                    </div>
                    {r.mitigation_plan && (
                      <div className="mt-2 p-2 rounded-lg text-xs" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
                        <strong>Mitigation:</strong> {r.mitigation_plan}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button onClick={()=>openEdit(r)} className="p-1.5 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}><PenIc /></button>
                    <button onClick={()=>setDeleteId(r.id)} className="p-1.5 rounded text-red-400"><TrIc /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)} title={editItem?'Edit Risk':'Log Risk'} size="lg">
        <div className="space-y-4">
          <FormField label="Risk Title" required>
            <input className="input" value={form.title} onChange={e=>f({title:e.target.value})} placeholder="Describe the risk briefly"/>
          </FormField>
          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description} onChange={e=>f({description:e.target.value})}/>
          </FormField>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Category">
              <select className="select" value={form.category} onChange={e=>f({category:e.target.value})}>
                {CATEGORY_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
            <FormField label="Impact">
              <select className="select" value={form.impact} onChange={e=>f({impact:e.target.value})}>
                {LEVEL_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
            <FormField label="Probability">
              <select className="select" value={form.probability} onChange={e=>f({probability:e.target.value})}>
                {LEVEL_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Mitigation Plan">
            <textarea className="input" rows={2} value={form.mitigation_plan} onChange={e=>f({mitigation_plan:e.target.value})} placeholder="Steps to reduce or eliminate this risk"/>
          </FormField>
          <FormField label="Contingency Plan">
            <textarea className="input" rows={2} value={form.contingency_plan} onChange={e=>f({contingency_plan:e.target.value})} placeholder="Actions if the risk materialises"/>
          </FormField>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Owner">
              <select className="select" value={form.owner_id} onChange={e=>f({owner_id:e.target.value})}>
                <option value="">Unassigned</option>
                {users.filter(u=>u.is_active).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </FormField>
            <FormField label="Status">
              <select className="select" value={form.status} onChange={e=>f({status:e.target.value})}>
                {STATUS_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>
            <FormField label="Next Review">
              <input type="date" className="input" value={form.review_date} onChange={e=>f({review_date:e.target.value})}/>
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!form.title} className="btn-primary flex items-center gap-2">
              {saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editItem?'Update Risk':'Log Risk'}
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!deleteId} onClose={()=>setDeleteId(null)} onConfirm={handleDelete} title="Delete Risk" message="Remove this risk from the register?"/>
    </div>
  );
}
